import React, { useEffect, useState } from 'react';
import './Subscription.css';
import { useAuth } from '../context/AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const PLAN = {
  name: 'Plan Mensual',
  price: '180.000',
  currency: 'ARS',
  period: '/ mes',
  features: [
    'Asistente de IA ilimitado',
    'Integración con WhatsApp Cloud API',
    'CRM y seguimiento de conversaciones',
    'Exportación de contactos y chats',
    'Entrenamiento con bases de datos',
    'Soporte prioritario',
  ],
};

const SETUP = {
  name: 'Implementación inicial',
  price: '300.000',
  currency: 'ARS',
  period: '',
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'No definida';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const getDaysLeft = (timestamp) => {
  if (!timestamp) return 0;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = date - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const Subscription = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    const status = searchParams.get('status');
    const payment = searchParams.get('payment');
    if (status === 'approved') {
      if (payment === 'setup') {
        toast.success('✅ ¡Pago aprobado! Implementación confirmada.');
      } else {
        toast.success('✅ ¡Pago aprobado! Tu suscripción fue renovada.');
      }
    } else if (status === 'rejected') {
      toast.error('❌ El pago fue rechazado. Intentalo de nuevo.');
    } else if (status === 'pending') {
      toast.info('⏳ Tu pago está pendiente de acreditación.');
    }
  }, [searchParams]);

  const handlePayMonthly = () => {
    if (!currentUser) return;
    navigate('/checkout?type=monthly');
  };

  const handlePaySetup = () => {
    if (!currentUser) return;
    navigate('/checkout?type=setup');
  };

  const expiresAt = userData?.accessExpiresAt;
  const daysLeft = getDaysLeft(expiresAt);
  const isActive = daysLeft > 0;

  const setupPaid = Boolean(userData?.setup_paid);
  const isExistingClient = userData?.clientType === 'internal';
  const showSetupOption = !setupPaid && !isExistingClient;

  const justApproved = searchParams.get('status') === 'approved';
  const justApprovedPayment = searchParams.get('payment');

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <p className="subscription-badge">SUSCRIPCIÓN</p>
        <h1 className="subscription-title">Tu plan actual</h1>
        <p className="subscription-subtitle">
          Gestioná y renová tu acceso a The Thing App
        </p>
      </div>

      <div className="subscription-content">
        {/* Estado actual */}
        <div className={`subscription-status-card ${isActive ? 'active' : 'expired'}`}>
          <div className="subscription-status-left">
            <span className={`subscription-status-badge ${isActive ? 'active' : 'expired'}`}>
              {isActive ? '● Activa' : '● Vencida'}
            </span>
            <p className="subscription-status-label">Vencimiento</p>
            <p className="subscription-status-date">
              {loading ? 'Cargando...' : formatDate(expiresAt)}
            </p>
          </div>
          {isActive && (
            <div className="subscription-status-right">
              <p className="subscription-days-number">{daysLeft}</p>
              <p className="subscription-days-label">días restantes</p>
            </div>
          )}
        </div>

        {/* Plan card */}
        <div className="subscription-plan-card">
          <div className="subscription-plan-header">
            <div>
              <p className="subscription-plan-name">{PLAN.name}</p>
    
              <div className="subscription-plan-price-row">
                <span className="subscription-plan-price">$ {PLAN.price}</span>
              </div>
              <p className="subscription-plan-fixed-label">INCLUYE 6 MIL CONVERSACIONES GRATIS</p>
              <p className="subscription-plan-footnote">*Por mes, no acumulables</p>
            </div>
            <div className="subscription-plan-glow" />
          </div>

          <ul className="subscription-features">
            {PLAN.features.map((f) => (
              <li key={f} className="subscription-feature-item">
                <span className="subscription-feature-check">✓</span>
                {f}
              </li>
            ))}
          </ul>

          {justApproved && justApprovedPayment === 'setup' && !setupPaid && (
            <div className="subscription-step-banner">
              <p className="subscription-step-banner-title">Pago confirmado.</p>
              <p className="subscription-step-banner-text">Último paso: activar abono mensual.</p>
            </div>
          )}

          <div className="subscription-pay-actions">
            <button
              className="subscription-pay-button"
              onClick={handlePayMonthly}
            >
              {isActive ? 'Renovar suscripción' : 'Activar suscripción'}
            </button>

            {showSetupOption && (
              <button
                className="subscription-setup-button"
                onClick={handlePaySetup}
              >
                Pagar {SETUP.name} ($ {SETUP.price})
              </button>
            )}
          </div>

          <p className="subscription-mp-note">
            🔒 Pago seguro procesado por{' '}
            <span className="subscription-mp-brand">MercadoPago</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
